import { PunchType } from '@construction/shared';

/**
 * 「我现在在不在班上」的合并判定。
 *
 * 为什么需要合并：**打卡是先入队再上传的**（离线也能打，CR-006/CR-015）。
 * 按下按钮的那一刻服务端还不知道有这条打卡，而界面上「下一步是上班还是下班」
 * 原来只看服务端 —— 于是工人点完「上班打卡」，按钮还是「上班打卡」，
 * 他自然会再点一次，服务端收到两条 IN，第二条判成 `DUPLICATE_IN` 进待复核。
 * 客户实测的就是这个：库里躺着成对的 `APPROVED` + `PENDING_REVIEW DUPLICATE_IN`，
 * 相隔 4 到 22 秒。
 *
 * 所以本地要记下「我刚打的那一张卡」，在服务端追上来之前由它说了算。
 * 判断「服务端追上来了没有」用的是时间戳而不是标志位：
 * 服务端最后一条打卡的时刻 >= 本地那条，就说明它已经收到了，本地记录作废。
 * 这样它自己会收敛，不需要谁去清标志。
 */

/** 本地记下的最后一次打卡。存 localStorage，按 userId 分键。 */
export interface LocalPunch {
  siteId: number;
  /**
   * CR-023 之前只记上班卡，所以旧记录没有这个字段 —— 缺省按 IN 处理。
   * 只记 IN 的后果是下班之后本地记录还在，工地一直锁着（服务端说没在班，
   * 而这里的兜底又说在），直到 24 小时过期。
   */
  type?: PunchType;
  occurredAt: string;
  /** 队列条目 id，排查时能和服务端记录对上 */
  clientId: string;
}

export interface ServerShift {
  open: boolean;
  punch: { siteId: number; occurredAt: string } | null;
}

export interface ResolvedShift {
  /** 正在上班 */
  open: boolean;
  /** 锁在哪个工地。null = 没在班，可以自由切工地 */
  lockedSiteId: number | null;
  /** 班次开始时刻（ISO）。计时器用它，只有 open 时有值 */
  startedAt: string | null;
  /** 这个状态还没被服务端确认 —— 那张卡还在上传队列里 */
  pendingSync: boolean;
  /** 本地记录已经被服务端追上，可以清掉了 */
  staleLocal: boolean;
}

/**
 * 合并服务端班次和本地待上传的打卡。
 *
 * @param server 服务端的 `/me/shift`，还没拿到就传 null（离线、首屏未回）
 * @param local  本地记下的最后一次打卡，没有就传 null
 */
export function resolveShiftState(
  server: ServerShift | null,
  local: LocalPunch | null,
): ResolvedShift {
  const serverAt = server?.punch ? Date.parse(server.punch.occurredAt) : null;
  const localAt = local ? Date.parse(local.occurredAt) : null;

  // 时间戳解析不出来的本地记录直接丢掉，别让 NaN 参与比较 ——
  // NaN 和任何数比都是 false，会静默退回「服务端说了算」
  const localUsable = local !== null && localAt !== null && Number.isFinite(localAt);

  // 服务端已经收到这条（或更新的）打卡 → 本地记录作废
  const serverCaughtUp = localUsable && serverAt !== null && serverAt >= localAt!;

  if (!localUsable || serverCaughtUp) {
    const open = server?.open ?? false;
    return {
      open,
      lockedSiteId: open ? (server?.punch?.siteId ?? null) : null,
      startedAt: open ? (server?.punch?.occurredAt ?? null) : null,
      pendingSync: false,
      staleLocal: localUsable && serverCaughtUp,
    };
  }

  // 本地那条比服务端知道的都新 —— 它还在队列里，由它说了算
  const open = (local!.type ?? PunchType.IN) === PunchType.IN;
  return {
    open,
    lockedSiteId: open ? local!.siteId : null,
    startedAt: open ? local!.occurredAt : null,
    pendingSync: true,
    staleLocal: false,
  };
}
